/**
 * PWA helpers — service-worker registration, install-prompt capture,
 * update notifications. Zero external deps.
 */

let deferredPromptEvent = null;
const listeners = new Set();

function emit() {
  for (const fn of listeners) fn(getInstallState());
}

function isStandalone() {
  return (
    (typeof window !== "undefined" &&
      (window.matchMedia?.("(display-mode: standalone)").matches ||
        window.matchMedia?.("(display-mode: window-controls-overlay)").matches)) ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ presents as Mac; check touch as extra signal
  const isTouchMac = /Macintosh/.test(ua) && "ontouchend" in document;
  return /iPad|iPhone|iPod/.test(ua) || isTouchMac;
}

export function getInstallState() {
  return {
    canInstall: !!deferredPromptEvent,
    isStandalone: isStandalone(),
    isIos: isIos(),
  };
}

export function subscribeInstallState(fn) {
  listeners.add(fn);
  fn(getInstallState());
  return () => listeners.delete(fn);
}

export async function promptInstall() {
  if (!deferredPromptEvent) return { outcome: "unavailable" };
  const ev = deferredPromptEvent;
  ev.prompt();
  const choice = await ev.userChoice.catch(() => ({ outcome: "dismissed" }));
  deferredPromptEvent = null;
  emit();
  return choice;
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Prevent the mini-infobar so we can show a custom UI.
    e.preventDefault();
    deferredPromptEvent = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    deferredPromptEvent = null;
    try { localStorage.setItem("jixxeo_pwa_installed", "1"); } catch {}
    emit();
  });
  // React to display-mode changes (e.g. user launches from home screen)
  try {
    window.matchMedia("(display-mode: standalone)").addEventListener("change", emit);
  } catch {}
}

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Only register on production-like origins (https or localhost)
  const proto = window.location.protocol;
  if (proto !== "https:" && window.location.hostname !== "localhost") return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((reg) => {
        // Handle waiting SW on load (new version installed but not active)
        if (reg.waiting) notifyUpdate(reg);
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener("statechange", () => {
            if (nw.state === "installed" && navigator.serviceWorker.controller) {
              notifyUpdate(reg);
            }
          });
        });
        // Check for updates once an hour
        setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);
      })
      .catch(() => {});
    // If the active SW changes (after skipWaiting) reload to pick up new assets
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}

const updateListeners = new Set();
let waitingReg = null;

function notifyUpdate(reg) {
  waitingReg = reg;
  for (const fn of updateListeners) fn();
}

export function subscribeUpdateAvailable(fn) {
  updateListeners.add(fn);
  if (waitingReg) fn();
  return () => updateListeners.delete(fn);
}

export function applyUpdate() {
  if (!waitingReg || !waitingReg.waiting) return;
  waitingReg.waiting.postMessage({ type: "SKIP_WAITING" });
}

// ============================================================
// Web Push
// ============================================================

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function getPushPermission() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function subscribeToPush(vapidPublicKey) {
  if (!isPushSupported()) throw new Error("Push not supported");
  const reg = await navigator.serviceWorker.ready;
  const key = urlBase64ToUint8Array(vapidPublicKey);
  let sub = await reg.pushManager.getSubscription();
  const cur = sub?.options?.applicationServerKey && new Uint8Array(sub.options.applicationServerKey);
  if (sub && cur && (cur.length !== key.length || cur.some((b, i) => b !== key[i]))) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key,
    });
  }
  return sub;
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  return endpoint;
}
